import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  BookOpen,
  Brain,
  Clock,
  ChevronRight,
  BookOpenCheck,
  GraduationCap,
} from 'lucide-react';

// Images
import View_Topic from '../../assets/View_Topic.png';
import View_HomeScreen from '../../assets/View_HomeScreen.png';
import View_Quiz from '../../assets/View_Quiz.png';
import View_Wiki from '../../assets/View_WIKI.png';
import View_Exam from '../../assets/View_Exam.png';

const steps = [
  {
    title: "01 | Informieren",
    description: "Verschaffe dir einen Überblick über die SKS-Prüfung in unserem kostenlosen Einsteiger-Guide und SKS-Wiki. Hier findest du alles, was du zum Start wissen musst.",
    image: View_Wiki,
    link: "/wiki/weg-zum-sks",
    linkText: "Unser Starter Wiki",
    icon: BookOpenCheck,
  },
  {
    title: "02 | Themenbereiche erkunden",
    description: "Tauche ein in die fünf Hauptkategorien: Navigation, Schifffahrtsrecht, Wetterkunde und Seemannschaft. Unser Wiki unterstützt dich mit kompaktem Grundlagenwissen.",
    image: View_Topic,
    icon: BookOpen,
  },
  {
    title: "03 | In kleinen Schritten lernen",
    description: "Bleibe flexibel mit unseren 10-Minuten-Lerneinheiten. Die Flashcard-Methode hält dich fokussiert und macht das Lernen effizient.",
    image: View_Quiz,
    icon: Clock,
  },
  {
    title: "04 | Gezielt wiederholen",
    description: "Unser intelligentes Spaced-Learning-System erkennt deinen Fortschritt und bringt wichtige Fragen zum optimalen Zeitpunkt zurück.",
    image: View_HomeScreen,
    icon: Brain,
  },
  {
    title: "05 | Prüfungsbereit werden",
    description: "Teste dein Wissen unter realen Bedingungen mit Original-Prüfungsbögen und sichere dir so den Erfolg.",
    image: View_Exam,
    icon: GraduationCap,
  }
];

const LearningSteps = () => {
  return (
    <section className="py-16 relative overflow-hidden">
      <div className="container mx-auto px-4">
        <motion.div
          className="text-center max-w-3xl mx-auto mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl font-bold mb-4">
            Dein Weg zur bestandenen SKS-Theorieprüfung
          </h2>
          <p className="text-lg text-base-content/70">
            Von der Vorbereitung bis zur bestandenen Prüfung – wir begleiten dich Schritt für Schritt
          </p>
        </motion.div>

        <div className="max-w-7xl mx-auto">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              className="mb-8 last:mb-0"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="card bg-base-100 shadow-lg hover:shadow-xl transition-all border border-base-300">
                <div className="card-body p-0">
                  <div className="grid md:grid-cols-2 items-stretch">
                    {/* Content Side */}
                    <div className="p-8 flex flex-col justify-center">
                      {/* Step Number Badge */}
                      <div className="flex items-start gap-4 mb-6">
                        <div className="w-14 h-14 rounded-2xl bg-primary/10 p-3 flex items-center justify-center shrink-0">
                          <step.icon className="w-8 h-8 text-primary" />
                        </div>
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-primary">
                            Schritt {index + 1}
                          </span>
                          <h3 className="text-xl font-bold mt-1">
                            {step.title.split('|')[1].trim()}
                          </h3>
                        </div>
                      </div>

                      {/* Description */}
                      <p className="text-base-content/70 text-lg leading-relaxed">
                        {step.description}
                      </p>

                      {/* Optional Link */}
                      {step.link && (
                        <Link 
                          to={step.link} 
                          className="inline-flex items-center gap-2 text-primary hover:text-primary/80 transition-colors mt-6 text-lg font-medium"
                        >
                          {step.linkText}
                          <ChevronRight className="w-5 h-5" />
                        </Link>
                      )}
                    </div>

                    {/* Image Side */}
                    <div className="relative bg-base-200/30 flex items-center justify-center p-8 min-h-[300px] border-t md:border-t-0 md:border-l border-base-300">
                      <div className="relative w-full max-w-lg mx-auto">
                        <div className="aspect-[4/3] w-full bg-base-100 rounded-2xl shadow-lg overflow-hidden">
                          <img
                            src={step.image}
                            alt={`Schritt ${index + 1}: ${step.title.split('|')[1].trim()}`}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default LearningSteps;